import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { flatten } from 'lodash';
import { getRequiredStatuses, isProfileCompleted } from '@pec/aion-ui-core/helpers/getStatus';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { useFeature } from 'flagged';
import { useSelector } from 'react-redux';

export const useOrganizationProfileNotifications = ({
  checkManageHiringClientConnections = true,
  includeServiceGroupsStatus = false
} = {}) => {
  const { organization } = useSelector((state: CommonRootState) => state.organization);
  const organizationInfoCountryIso2Code = useSelector(
    (state: CommonRootState) => state.organizationInfo?.organizationInfo?.countryIso2Code
  );
  const addressCountryIso2Code = useSelector(
    (state: CommonRootState) =>
      (state as CommonRootState & { organizationDetails?: { countryIso2Code?: string } }).organizationDetails
        ?.countryIso2Code
  );
  const { organizationProfile } = useSelector((state: CommonRootState) => state.notifications);
  const { areAllServiceGroupsCompleted } = useSelector((state: CommonRootState) => state.activeConnections);

  const aionOperatingRegionsV2 = useFeature('AionOperatingRegionsV2');
  const servicesProvidedV2 = useFeature('ServicesProvidedV2');
  const manageHiringClientConnections = organization?.features.includes(
    OrganizationFeature.ManageHiringClientConnections
  );
  const chasOrganizationFields = organization?.features.includes(OrganizationFeature.ChasOrganizationFields);
  const profileCountryIso2Code = addressCountryIso2Code ?? organizationInfoCountryIso2Code;
  const creditSafeStatus = organizationProfile.operationDetailsSection.creditSafeNumberIsCompleted;
  const shouldIncludeCreditSafeNumber = profileCountryIso2Code
    ? profileCountryIso2Code === 'US'
    : creditSafeStatus !== null;

  let statuses = getRequiredStatuses(
    organizationProfile,
    Boolean(aionOperatingRegionsV2),
    Boolean(servicesProvidedV2),
    checkManageHiringClientConnections ? Boolean(manageHiringClientConnections) : false,
    Boolean(chasOrganizationFields)
  );

  if (!shouldIncludeCreditSafeNumber) {
    statuses = {
      ...statuses,
      operationDetailsSection: statuses.operationDetailsSection.filter(([key]) => key !== 'creditSafeNumberIsCompleted')
    };
  }

  const isCompleted = isProfileCompleted(flatten(Object.values(statuses)));
  const isFetching = Object.values(organizationProfile).every(statuses =>
    Object.values(statuses).every(status => status === null)
  );

  if (!isFetching && includeServiceGroupsStatus && statuses.hiringClientSection) {
    statuses.hiringClientSection = [
      ...statuses.hiringClientSection,
      ['serviceGroupsCompleted', areAllServiceGroupsCompleted]
    ];
  }

  return { statuses, isFetching, isProfileCompleted: isCompleted };
};
